/**
 * MODAL: PRICING ITEM
 * Création / édition d'un item de grille tarifaire (/admin/pricing).
 * Le kind (obligatoire / option) est fixé par la section d'origine, il n'est
 * pas éditable ici. Montant saisi en euros, converti en centimes au submit
 * par la page (eurosToCents).
 */

import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { centsToEuros } from '../../lib/pricing/money';
import type { PricingItemFormValues } from '../../schemas/pricingItem';
import { pricingItemFormSchema } from '../../schemas/pricingItem';
import type { PricingItem, PricingItemKind } from '../../types/pricingItem';
import { Form, FormInput } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';

interface PricingItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  kind: PricingItemKind;
  /** null = création, sinon édition de cet item */
  item: PricingItem | null;
  /** Résout `true` uniquement quand la mutation est réellement committée. */
  onSubmit: (values: PricingItemFormValues) => Promise<boolean>;
}

function modalTitle(kind: PricingItemKind, isEdit: boolean): string {
  if (isEdit) {
    return kind === 'mandatory' ? "Modifier l'item obligatoire" : "Modifier l'option";
  }
  return kind === 'mandatory' ? 'Ajouter un item obligatoire' : 'Ajouter une option';
}

export function PricingItemModal({ isOpen, onClose, kind, item, onSubmit }: PricingItemModalProps) {
  const form = useForm<PricingItemFormValues>({
    resolver: zodResolver(pricingItemFormSchema),
    defaultValues: { label: '', code: '' },
  });

  // Re-remplit le formulaire à chaque ouverture (création = vide, édition =
  // valeurs de l'item, montant reconverti en euros).
  useEffect(() => {
    if (isOpen) {
      form.reset(
        item
          ? {
              label: item.label,
              code: item.code ?? '',
              amount_euros: centsToEuros(item.amount_cents),
              conditions_url: item.conditions_url ?? '',
            }
          : { label: '', code: '', conditions_url: '' }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (values: PricingItemFormValues) => {
    // Ne ferme le modal (et ne perd la saisie) que si l'écriture a abouti :
    // échec CRUD, vérif BED impossible ou avertissement différé => on reste ouvert.
    const committed = await onSubmit(values);
    if (committed) handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle(kind, item !== null)}
      size="md"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel={item ? 'Enregistrer' : 'Ajouter'}
          isSubmitting={form.formState.isSubmitting}
          formId="pricing-item-form"
        />
      }
    >
      <Form {...form}>
        <form id="pricing-item-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="label"
              label="Libellé"
              required
              placeholder={kind === 'mandatory' ? 'Ex: Engagement' : 'Ex: Assurance annulation'}
            />

            <FormInput
              control={form.control}
              name="amount_euros"
              label="Montant (EUR)"
              type="number"
              step="0.01"
              required
              placeholder="Ex: 3500"
              valueAsNumber
            />

            <FormInput
              control={form.control}
              name="code"
              label="Code métier (facultatif)"
              placeholder="Ex: engagement"
            />

            <div className="flex flex-col gap-1">
              <FormInput
                control={form.control}
                name="conditions_url"
                label="URL des conditions (assurance, facultatif)"
                placeholder="https://… (document fourni par l'assureur)"
              />
              <p className="text-xs text-zinc-400">
                Si renseignée, le participant devra certifier avoir lu ce document avant de payer
                son BED (exigence de la compagnie d'assurance).
              </p>
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
